import { forwardRef, useCallback } from "react"

// --- Lib ---
import { parseShortcutKeys } from "@/tiptap/lib/tiptap-utils"

// --- Hooks ---
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"

import {
  INDENT_SHORTCUT_KEYS,
  useIndent,
} from "@/tiptap/ui/indent-button"

import { Button } from "@/tiptap/ui-primitive/button"
import { Badge } from "@/tiptap/ui-primitive/badge"

export function IndentShortcutBadge({
  action,
  shortcutKeys = INDENT_SHORTCUT_KEYS[action]
}) {
  return <Badge>{parseShortcutKeys({ shortcutKeys })}</Badge>;
}

/**
 * Button component for controlling indentation in a Tiptap editor.
 *
 * For custom button implementations, use the `useIndent` hook instead.
 */
export const IndentButton = forwardRef((
  {
    editor: providedEditor,
    action,
    text,
    hideWhenUnavailable = false,
    onIndented,
    showShortcut = false,
    onClick,
    icon: CustomIcon,
    children,
    ...buttonProps
  },
  ref
) => {
  const { editor } = useTiptapEditor(providedEditor)
  const { isVisible, handleIndent, label, canIndent, Icon, shortcutKeys } =
    useIndent({
      editor,
      action,
      hideWhenUnavailable,
      onIndented,
    })

  const handleClick = useCallback((event) => {
    onClick?.(event)
    if (event.defaultPrevented) return
    handleIndent()
  }, [handleIndent, onClick])

  if (!isVisible) {
    return null
  }

  const RenderIcon = CustomIcon ?? Icon

  return (
    <Button
      type="button"
      disabled={!canIndent}
      variant="ghost"
      data-disabled={!canIndent}
      role="button"
      tabIndex={-1}
      aria-label={label}
      tooltip={label}
      onClick={handleClick}
      {...buttonProps}
      ref={ref}>
      {children ?? (
        <>
          <RenderIcon className="tiptap-button-icon" />
          {text && <span className="tiptap-button-text">{text}</span>}
          {showShortcut && (
            <IndentShortcutBadge action={action} shortcutKeys={shortcutKeys} />
          )}
        </>
      )}
    </Button>
  );
})

IndentButton.displayName = "IndentButton"
